import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  FaCheck, FaTimes, FaSpinner,
  FaExclamationTriangle, FaCheckCircle, FaMagic, FaServer,
  FaShieldAlt, FaChevronRight, FaRoute, FaInfoCircle,
  FaGlobe, FaLock, FaExchangeAlt, FaCopy
} from 'react-icons/fa';
import './RouterWizards.scss';
import * as Sentry from '@sentry/react';

const useApiCall = () => {
  const { apiFetch } = useAuth();
  return useCallback(async (url, opts = {}) => {
    const response = await apiFetch(url, {
      headers: { 'Content-Type': 'application/json', ...opts.headers },
      ...opts
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  }, [apiFetch]);
};

// =====================================================================
// DHCP Server Setup Wizard
// =====================================================================
const DhcpWizard = ({ routerId, onClose, onSuccess }) => {
  const apiCall = useApiCall();
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [interfaces, setInterfaces] = useState([]);
  const [form, setForm] = useState({
    serverName: 'dhcp1',
    interface: '',
    poolName: 'dhcp-pool1',
    poolStart: '',
    poolEnd: '',
    networkAddress: '',
    gateway: '',
    dnsServers: '8.8.8.8,8.8.4.4',
    leaseTime: '1d'
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await apiCall(`/api/RouterConfig/${routerId}/interfaces`);
        setInterfaces(data.data || []);
      }
      catch (catchError) {
          Sentry.captureException(catchError);
          /* ignore */
      }
    })();
  }, [routerId, apiCall]);

  const inferNetwork = (iface) => {
    // Try to auto-fill from interface addresses
    setForm(f => ({ ...f, interface: iface }));
  };

  const poolRanges = form.poolStart && form.poolEnd ? `${form.poolStart}-${form.poolEnd}` : '';

  const canProceed = () => {
    if (step === 1) return !!form.interface;
    if (step === 2) return !!form.networkAddress && !!form.gateway && !!form.poolStart && !!form.poolEnd;
    return true;
  };

  const handleExecute = async () => {
    setRunning(true);
    setError(null);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/wizard/dhcp`, {
        method: 'POST',
        body: JSON.stringify({
          serverName: form.serverName,
          interface: form.interface,
          poolName: form.poolName,
          poolRanges,
          networkAddress: form.networkAddress,
          gateway: form.gateway,
          dnsServers: form.dnsServers,
          leaseTime: form.leaseTime
        })
      });
      setResult('success');
      onSuccess?.();
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
      setResult('error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="wizard">
      <div className="wizard__header">
        <FaServer className="wizard__icon" />
        <div>
          <h3>DHCP Server Setup Wizard</h3>
          <p>Configure a complete DHCP server with IP pool and network settings</p>
        </div>
        <button className="btn-icon wizard__close" onClick={onClose}><FaTimes /></button>
      </div>

      <div className="wizard__progress">
        {['Interface', 'Network', 'Confirm'].map((label, i) => (
          <div key={i} className={`wizard__step ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : ''}`}>
            <span className="wizard__step-num">{step > i + 1 ? <FaCheck /> : i + 1}</span>
            <span className="wizard__step-label">{label}</span>
          </div>
        ))}
      </div>

      <div className="wizard__body">
        {result ? (
          <div className={`wizard__result wizard__result--${result}`}>
            {result === 'success' ? (
              <><FaCheckCircle size={32} /> <h4>DHCP Server Created Successfully</h4>
                <p>Server <strong>{form.serverName}</strong> is now active on <strong>{form.interface}</strong> serving addresses from the <strong>{form.poolName}</strong> pool.</p></>
            ) : (
              <><FaExclamationTriangle size={32} /> <h4>Wizard Failed</h4><p>{error}</p></>
            )}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : step === 1 ? (
          <div className="wizard__form">
            <div className="form-group">
              <label>Server Name</label>
              <input value={form.serverName} onChange={e => setForm(f => ({ ...f, serverName: e.target.value }))} placeholder="dhcp1" />
            </div>
            <div className="form-group">
              <label>Interface</label>
              <select value={form.interface} onChange={e => inferNetwork(e.target.value)}>
                <option value="">Select interface...</option>
                {interfaces.filter(i => i.type !== 'loopback').map(i => (
                  <option key={i.name} value={i.name}>{i.name} ({i.type})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Pool Name</label>
              <input value={form.poolName} onChange={e => setForm(f => ({ ...f, poolName: e.target.value }))} placeholder="dhcp-pool1" />
            </div>
          </div>
        ) : step === 2 ? (
          <div className="wizard__form">
            <div className="form-row-2">
              <div className="form-group">
                <label>Network Address (CIDR)</label>
                <input value={form.networkAddress} onChange={e => setForm(f => ({ ...f, networkAddress: e.target.value }))}
                  placeholder="192.168.88.0/24" />
              </div>
              <div className="form-group">
                <label>Gateway</label>
                <input value={form.gateway} onChange={e => setForm(f => ({ ...f, gateway: e.target.value }))}
                  placeholder="192.168.88.1" />
              </div>
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Pool Start</label>
                <input value={form.poolStart} onChange={e => setForm(f => ({ ...f, poolStart: e.target.value }))}
                  placeholder="192.168.88.100" />
              </div>
              <div className="form-group">
                <label>Pool End</label>
                <input value={form.poolEnd} onChange={e => setForm(f => ({ ...f, poolEnd: e.target.value }))}
                  placeholder="192.168.88.254" />
              </div>
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>DNS Servers</label>
                <input value={form.dnsServers} onChange={e => setForm(f => ({ ...f, dnsServers: e.target.value }))}
                  placeholder="8.8.8.8,8.8.4.4" />
              </div>
              <div className="form-group">
                <label>Lease Time</label>
                <select value={form.leaseTime} onChange={e => setForm(f => ({ ...f, leaseTime: e.target.value }))}>
                  <option value="30m">30 Minutes</option>
                  <option value="1h">1 Hour</option>
                  <option value="8h">8 Hours</option>
                  <option value="1d">1 Day</option>
                  <option value="3d">3 Days</option>
                  <option value="7d">7 Days</option>
                </select>
              </div>
            </div>
          </div>
        ) : (
          <div className="wizard__summary">
            <h4>Configuration Summary</h4>
            <div className="summary-grid">
              <div className="summary-item"><span className="summary-label">Server Name</span><span className="summary-value">{form.serverName}</span></div>
              <div className="summary-item"><span className="summary-label">Interface</span><span className="summary-value">{form.interface}</span></div>
              <div className="summary-item"><span className="summary-label">Network</span><span className="summary-value mono">{form.networkAddress}</span></div>
              <div className="summary-item"><span className="summary-label">Gateway</span><span className="summary-value mono">{form.gateway}</span></div>
              <div className="summary-item"><span className="summary-label">Pool</span><span className="summary-value mono">{poolRanges}</span></div>
              <div className="summary-item"><span className="summary-label">DNS</span><span className="summary-value mono">{form.dnsServers}</span></div>
              <div className="summary-item"><span className="summary-label">Lease Time</span><span className="summary-value">{form.leaseTime}</span></div>
            </div>
            <div className="wizard__info">
              <FaInfoCircle /> This will create an IP pool, DHCP network, and DHCP server on the router.
            </div>
          </div>
        )}
      </div>

      {!result && (
        <div className="wizard__footer">
          {step > 1 && <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
          <div className="wizard__footer-spacer" />
          {step < 3 ? (
            <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} disabled={!canProceed()}>
              Next <FaChevronRight />
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleExecute} disabled={running}>
              {running ? <><FaSpinner className="spin" /> Creating...</> : <><FaMagic /> Create DHCP Server</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Failover Wizard — Recursive routing failover between breakouts
// =====================================================================
const FailoverWizard = ({ routerId, onClose, onSuccess }) => {
  const apiCall = useApiCall();
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    primaryGateway: '',
    backupGateway: '',
    primaryCheckAddress: '1.1.1.1',
    backupCheckAddress: '8.8.8.8',
    primaryDistance: '1',
    backupDistance: '2',
    comment: 'CapTrac Failover'
  });

  const canProceed = () => {
    if (step === 1) return !!form.primaryGateway && !!form.backupGateway;
    return true;
  };

  const handleExecute = async () => {
    setRunning(true);
    setError(null);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/wizard/failover`, {
        method: 'POST', body: JSON.stringify(form)
      });
      setResult('success');
      onSuccess?.();
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setResult('error');
    }
    finally { setRunning(false); }
  };

  return (
    <div className="wizard">
      <div className="wizard__header">
        <FaRoute className="wizard__icon" />
        <div>
          <h3>Internet Failover Wizard</h3>
          <p>Configure automatic failover between two internet breakouts using recursive routing</p>
        </div>
        <button className="btn-icon wizard__close" onClick={onClose}><FaTimes /></button>
      </div>

      <div className="wizard__progress">
        {['Gateways', 'Check Hosts', 'Confirm'].map((label, i) => (
          <div key={i} className={`wizard__step ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : ''}`}>
            <span className="wizard__step-num">{step > i + 1 ? <FaCheck /> : i + 1}</span>
            <span className="wizard__step-label">{label}</span>
          </div>
        ))}
      </div>

      <div className="wizard__body">
        {result ? (
          <div className={`wizard__result wizard__result--${result}`}>
            {result === 'success' ? (
              <><FaCheckCircle size={32} />
                <h4>Failover Routes Configured</h4>
                <p>Recursive routing failover is active. If <strong>{form.primaryCheckAddress}</strong> becomes unreachable via the primary gateway, traffic will automatically shift to the backup gateway.</p>
                <div className="wizard__info"><FaInfoCircle /> 4 routes were created: 2 check-host routes and 2 recursive default routes with check-gateway=ping.</div></>
            ) : (
              <><FaExclamationTriangle size={32} /><h4>Wizard Failed</h4><p>{error}</p></>
            )}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : step === 1 ? (
          <div className="wizard__form">
            <div className="wizard__info" style={{ marginBottom: 16 }}>
              <FaInfoCircle /> Enter the next-hop gateway IPs for your primary and backup internet connections (e.g., ISP gateway addresses).
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Primary Gateway (ISP 1)</label>
                <input value={form.primaryGateway} onChange={e => setForm(f => ({ ...f, primaryGateway: e.target.value }))}
                  placeholder="10.0.0.1" />
              </div>
              <div className="form-group">
                <label>Backup Gateway (ISP 2)</label>
                <input value={form.backupGateway} onChange={e => setForm(f => ({ ...f, backupGateway: e.target.value }))}
                  placeholder="10.0.1.1" />
              </div>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="wizard__form">
            <div className="wizard__info" style={{ marginBottom: 16 }}>
              <FaInfoCircle /> Check-host addresses are used to verify gateway reachability. Each address is routed through its respective gateway. When unreachable, the recursive default route via that gateway is withdrawn.
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Primary Check Host</label>
                <input value={form.primaryCheckAddress} onChange={e => setForm(f => ({ ...f, primaryCheckAddress: e.target.value }))}
                  placeholder="1.1.1.1" />
                <small className="muted">Routed via primary gateway</small>
              </div>
              <div className="form-group">
                <label>Backup Check Host</label>
                <input value={form.backupCheckAddress} onChange={e => setForm(f => ({ ...f, backupCheckAddress: e.target.value }))}
                  placeholder="8.8.8.8" />
                <small className="muted">Routed via backup gateway</small>
              </div>
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Primary Distance</label>
                <input type="number" value={form.primaryDistance} onChange={e => setForm(f => ({ ...f, primaryDistance: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Backup Distance</label>
                <input type="number" value={form.backupDistance} onChange={e => setForm(f => ({ ...f, backupDistance: e.target.value }))} />
              </div>
            </div>
          </div>
        ) : (
          <div className="wizard__summary">
            <h4>Failover Configuration Summary</h4>
            <div className="summary-grid">
              <div className="summary-item"><span className="summary-label">Primary Gateway</span><span className="summary-value mono">{form.primaryGateway}</span></div>
              <div className="summary-item"><span className="summary-label">Backup Gateway</span><span className="summary-value mono">{form.backupGateway}</span></div>
              <div className="summary-item"><span className="summary-label">Primary Check Host</span><span className="summary-value mono">{form.primaryCheckAddress}</span></div>
              <div className="summary-item"><span className="summary-label">Backup Check Host</span><span className="summary-value mono">{form.backupCheckAddress}</span></div>
              <div className="summary-item"><span className="summary-label">Primary Distance</span><span className="summary-value">{form.primaryDistance}</span></div>
              <div className="summary-item"><span className="summary-label">Backup Distance</span><span className="summary-value">{form.backupDistance}</span></div>
            </div>
            <div className="wizard__info">
              <FaInfoCircle /> This creates recursive check-gateway routes. If the primary path fails (ping to check host times out), traffic automatically fails over to the backup path.
            </div>
          </div>
        )}
      </div>

      {!result && (
        <div className="wizard__footer">
          {step > 1 && <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
          <div className="wizard__footer-spacer" />
          {step < 3 ? (
            <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} disabled={!canProceed()}>
              Next <FaChevronRight />
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleExecute} disabled={running}>
              {running ? <><FaSpinner className="spin" /> Configuring...</> : <><FaMagic /> Apply Failover Routes</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// VRRP Wizard — Hot failover between two routers
// =====================================================================
const VrrpWizard = ({ routerId, onClose, onSuccess }) => {
  const apiCall = useApiCall();
  const { apiFetch } = useAuth();
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [interfaces, setInterfaces] = useState([]);
  const [routers, setRouters] = useState([]);
  const [form, setForm] = useState({
    vrrpName: 'vrrp1',
    interface: '',
    vrid: '1',
    priority: '100',
    virtualIp: '',
    isMaster: true,
    authPassword: '',
    peerRouterId: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [ifData, routerData] = await Promise.all([
          apiCall(`/api/RouterConfig/${routerId}/interfaces`),
          apiFetch('/api/RouterConfig').then(r => r.json())
        ]);
        setInterfaces(ifData.data || []);
        const allRouters = routerData.data || (Array.isArray(routerData) ? routerData : []);
        setRouters(allRouters.filter(r => r.id !== parseInt(routerId)));
      }
      catch (catchError) {
          Sentry.captureException(catchError);
          /* ignore */
      }
    })();
  }, [routerId, apiCall, apiFetch]);

  const canProceed = () => {
    if (step === 1) return !!form.interface && !!form.virtualIp;
    if (step === 2) return !!form.vrid;
    return true;
  };

  const handleExecute = async () => {
    setRunning(true);
    setError(null);
    try {
      // Configure VRRP on this router
      await apiCall(`/api/RouterConfig/${routerId}/wizard/vrrp`, {
        method: 'POST', body: JSON.stringify(form)
      });

      // If a peer router is selected, configure it as backup
      if (form.peerRouterId) {
        try {
          await apiCall(`/api/RouterConfig/${form.peerRouterId}/wizard/vrrp`, {
            method: 'POST',
            body: JSON.stringify({
              ...form,
              isMaster: !form.isMaster,
              priority: form.isMaster ? '50' : '100'
            })
          });
        } catch (peerErr) {
            Sentry.captureException(peerErr);
          setError(`This router configured successfully, but peer failed: ${peerErr.message}`);
          setResult('error');
          setRunning(false);
          return;
        }
      }

      setResult('success');
      onSuccess?.();
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setResult('error');
    }
    finally { setRunning(false); }
  };

  return (
    <div className="wizard">
      <div className="wizard__header">
        <FaShieldAlt className="wizard__icon" />
        <div>
          <h3>VRRP Failover Wizard</h3>
          <p>Configure VRRP hot-standby failover between two routers sharing a virtual IP</p>
        </div>
        <button className="btn-icon wizard__close" onClick={onClose}><FaTimes /></button>
      </div>

      <div className="wizard__progress">
        {['Interface & VIP', 'VRRP Settings', 'Confirm'].map((label, i) => (
          <div key={i} className={`wizard__step ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : ''}`}>
            <span className="wizard__step-num">{step > i + 1 ? <FaCheck /> : i + 1}</span>
            <span className="wizard__step-label">{label}</span>
          </div>
        ))}
      </div>

      <div className="wizard__body">
        {result ? (
          <div className={`wizard__result wizard__result--${result}`}>
            {result === 'success' ? (
              <><FaCheckCircle size={32} />
                <h4>VRRP Configured Successfully</h4>
                <p>Virtual IP <strong>{form.virtualIp}</strong> is now managed by VRRP (VRID {form.vrid}).
                  {form.peerRouterId ? ' Both routers have been configured.' : ' Configure the peer router as backup for full redundancy.'}</p></>
            ) : (
              <><FaExclamationTriangle size={32} /><h4>Configuration Issue</h4><p>{error}</p></>
            )}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : step === 1 ? (
          <div className="wizard__form">
            {/* Step 1 contextual explanation — helps operators understand what VRRP is and why each field matters */}
            <div className="wizard__explain">
              <div className="wizard__explain-title">
                <FaInfoCircle /> What is VRRP and why do I need it?
              </div>
              <p>
                <strong>VRRP (Virtual Router Redundancy Protocol)</strong> allows two or more routers to share a single
                virtual IP address. All clients on the network use that virtual IP as their default gateway. Because
                the virtual IP is managed by VRRP rather than tied to one physical router, if the active
                (&quot;Master&quot;) router goes offline the standby (&quot;Backup&quot;) router automatically
                takes ownership of the virtual IP — usually within two seconds — with no changes required on any
                client device.
              </p>
              <ul className="wizard__explain-list">
                <li>
                  <strong>Interface:</strong> The LAN-facing network port that clients use to reach their gateway.
                  VRRP will create a virtual IP on this interface and listen for heartbeat advertisements from peer
                  routers. Do <em>not</em> select a WAN or loopback interface.
                </li>
                <li>
                  <strong>Virtual IP (CIDR):</strong> The shared IP address that clients treat as their gateway
                  (e.g., <code>192.168.1.1/24</code>). This address floats between Master and Backup — it is never
                  permanently assigned to any single router. Clients do not need to change their gateway setting
                  when a failover occurs.
                </li>
                <li>
                  <strong>Router Role — Master vs. Backup:</strong> Only one router can be Master at a time.
                  The Master actively handles traffic; the Backup monitors the Master via periodic heartbeats and
                  promotes itself only when the Master stops responding. Set this router as Master if it is the
                  primary device. Set it as Backup if you are running this wizard on the secondary device.
                </li>
              </ul>
            </div>

            <div className="form-group">
              <label>Interface</label>
              <select value={form.interface} onChange={e => setForm(f => ({ ...f, interface: e.target.value }))}>
                <option value="">Select interface...</option>
                {interfaces.filter(i => i.type !== 'loopback').map(i => (
                  <option key={i.name} value={i.name}>{i.name} ({i.type})</option>
                ))}
              </select>
              <small className="muted">Select the LAN-facing interface where the virtual IP will be hosted</small>
            </div>
            <div className="form-group">
              <label>Virtual IP Address (CIDR)</label>
              <input value={form.virtualIp} onChange={e => setForm(f => ({ ...f, virtualIp: e.target.value }))}
                placeholder="192.168.1.1/24" />
              <small className="muted">
                The shared gateway IP that floats between Master and Backup — clients always point here regardless of which
                physical router is active
              </small>
            </div>
            <div className="form-group">
              <label>This Router's Role</label>
              <div className="role-toggle">
                <button className={`btn btn-sm ${form.isMaster ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setForm(f => ({ ...f, isMaster: true, priority: '100' }))}>
                  Master (Priority 100)
                </button>
                <button className={`btn btn-sm ${!form.isMaster ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setForm(f => ({ ...f, isMaster: false, priority: '50' }))}>
                  Backup (Priority 50)
                </button>
              </div>
              <small className="muted">
                Master = active router handling traffic. Backup = hot-standby that promotes itself if Master fails.
                Higher priority wins the election — Master should always have the highest priority.
              </small>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="wizard__form">
            {/* Step 2 contextual explanation — helps operators understand VRID, auth, and the peer auto-config option */}
            <div className="wizard__explain">
              <div className="wizard__explain-title">
                <FaInfoCircle /> VRRP group identity and peer configuration
              </div>
              <p>
                For failover to work correctly, <strong>both the Master and Backup routers must be in the same
                VRRP group</strong>. A group is defined by the combination of interface, VRID, and virtual IP.
                Routers that do not share the same VRID will not recognize each other and no failover will occur.
              </p>
              <ul className="wizard__explain-list">
                <li>
                  <strong>VRRP Name:</strong> A human-readable identifier used internally by MikroTik RouterOS.
                  It must be unique on each router. Use a descriptive name such as <code>vrrp-lan</code> or
                  <code>vrrp-gateway</code> to make the config easy to identify later.
                </li>
                <li>
                  <strong>VRID (Virtual Router ID):</strong> A number between 1 and 255 that uniquely identifies
                  this VRRP group on the network. <em>This value must be identical on every router participating
                  in the same failover group.</em> Different VRRP groups on the same interface must use different
                  VRIDs. If you only have one VRRP group, leaving this as <code>1</code> is fine.
                </li>
                <li>
                  <strong>Authentication Password:</strong> An optional shared secret that prevents rogue routers
                  from joining your failover group. Only VRRPv2 supports this — if you are using VRRPv3 (the
                  default on RouterOS 7) authentication is not supported per RFC 5798 and this field will be
                  ignored. Leave blank unless you specifically need VRRPv2 compatibility.
                </li>
                <li>
                  <strong>Peer Router (auto-configure):</strong> If you select a peer here, the wizard will open
                  an SSH session to that router and configure VRRP on it automatically using the <em>opposite</em>{' '}
                  role and the same VRID and virtual IP — saving you from having to run this wizard a second time.
                  Leave blank if you prefer to configure the peer manually or if it is not yet registered in CapTrac.
                </li>
              </ul>
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label>VRRP Name</label>
                <input value={form.vrrpName} onChange={e => setForm(f => ({ ...f, vrrpName: e.target.value }))}
                  placeholder="vrrp1" />
                <small className="muted">Unique RouterOS identifier for this VRRP interface (e.g. vrrp-lan)</small>
              </div>
              <div className="form-group">
                <label>VRID (Virtual Router ID)</label>
                <input type="number" min="1" max="255" value={form.vrid}
                  onChange={e => setForm(f => ({ ...f, vrid: e.target.value }))} />
                <small className="muted">
                  <strong>Must be identical on both routers</strong> — this is how they find each other (1–255)
                </small>
              </div>
            </div>
            <div className="form-group">
              <label>Authentication Password (optional — VRRPv2 only)</label>
              <input value={form.authPassword} onChange={e => setForm(f => ({ ...f, authPassword: e.target.value }))}
                placeholder="Leave blank for no authentication" />
              <small className="muted">
                Shared secret to prevent rogue routers joining this group. Must match on all peers.
                Not supported by VRRPv3 (RFC 5798) — leave blank when using RouterOS 7 default settings.
              </small>
            </div>
            <div className="form-group">
              <label>Peer Router (optional — auto-configure backup)</label>
              <select value={form.peerRouterId} onChange={e => setForm(f => ({ ...f, peerRouterId: e.target.value }))}>
                <option value="">— Configure peer manually —</option>
                {routers.map(r => (
                  <option key={r.id} value={r.id}>{r.displayName || r.hostname || r.ipAddress}</option>
                ))}
              </select>
              <small className="muted">
                Select a router to have the wizard automatically configure it as the{' '}
                {form.isMaster ? 'Backup' : 'Master'} with matching VRID and virtual IP via SSH
              </small>
            </div>
          </div>
        ) : (
          <div className="wizard__summary">
            {/* Step 3 explanation — confirms what is about to happen and what the outcome will be */}
            <div className="wizard__explain wizard__explain--confirm">
              <div className="wizard__explain-title">
                <FaInfoCircle /> What will happen when you click &quot;Configure VRRP&quot;?
              </div>
              <p>
                The wizard will push the VRRP configuration below to this router via SSH. MikroTik RouterOS will
                create a virtual VRRP interface on <strong>{form.interface || 'the selected interface'}</strong> and
                begin sending heartbeat advertisements. Once configured:
              </p>
              <ul className="wizard__explain-list">
                <li>
                  Clients should update their default gateway to <strong>{form.virtualIp || 'the virtual IP'}</strong>{' '}
                  (or confirm their DHCP server hands out this address).
                </li>
                <li>
                  This router will act as the <strong>{form.isMaster ? 'Master (active)' : 'Backup (standby)'}</strong>.
                  {form.isMaster
                    ? ' It will handle all traffic until it fails or is shut down.'
                    : ' It will monitor the Master and automatically promote itself if the Master stops responding.'}
                </li>
                {form.peerRouterId ? (
                  <li>
                    The peer router (<strong>
                      {routers.find(r => r.id === parseInt(form.peerRouterId))?.displayName || `Router #${form.peerRouterId}`}
                    </strong>) will be configured simultaneously as the{' '}
                    <strong>{form.isMaster ? 'Backup (priority 50)' : 'Master (priority 100)'}</strong> using the
                    same VRID and virtual IP. Both SSH sessions must succeed for full redundancy.
                  </li>
                ) : (
                  <li>
                    No peer router was selected — you will need to configure the backup router separately to complete
                    the failover cluster.
                  </li>
                )}
                <li>
                  <strong>No client downtime is expected</strong> during this configuration provided no failover
                  event is triggered while the wizard is running.
                </li>
              </ul>
            </div>

            <h4>VRRP Configuration Summary</h4>
            <div className="summary-grid">
              <div className="summary-item"><span className="summary-label">VRRP Name</span><span className="summary-value">{form.vrrpName}</span></div>
              <div className="summary-item"><span className="summary-label">Interface</span><span className="summary-value">{form.interface}</span></div>
              <div className="summary-item"><span className="summary-label">Virtual IP</span><span className="summary-value mono">{form.virtualIp}</span></div>
              <div className="summary-item"><span className="summary-label">VRID</span><span className="summary-value">{form.vrid}</span></div>
              <div className="summary-item"><span className="summary-label">Role</span><span className="summary-value">{form.isMaster ? 'Master (active)' : 'Backup (standby)'}</span></div>
              <div className="summary-item"><span className="summary-label">Priority</span><span className="summary-value">{form.priority}</span></div>
              {form.peerRouterId && (
                <div className="summary-item"><span className="summary-label">Peer Router</span>
                  <span className="summary-value">{routers.find(r => r.id === parseInt(form.peerRouterId))?.displayName || form.peerRouterId}</span>
                </div>
              )}
            </div>
            {form.peerRouterId && (
              <div className="wizard__info">
                <FaInfoCircle /> The peer router will be auto-configured as {form.isMaster ? 'Backup (priority 50)' : 'Master (priority 100)'} with the same VRID and virtual IP.
              </div>
            )}
          </div>
        )}
      </div>

      {!result && (
        <div className="wizard__footer">
          {step > 1 && <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
          <div className="wizard__footer-spacer" />
          {step < 3 ? (
            <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} disabled={!canProceed()}>
              Next <FaChevronRight />
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleExecute} disabled={running}>
              {running ? <><FaSpinner className="spin" /> Configuring...</> : <><FaMagic /> Configure VRRP</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// NAT Masquerade Wizard
// =====================================================================
const NatMasqueradeWizard = ({ routerId, onClose, onSuccess }) => {
  const apiCall = useApiCall();
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [interfaces, setInterfaces] = useState([]);
  const [form, setForm] = useState({
    outInterface: '',
    srcAddressList: '',
    comment: ''
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await apiCall(`/api/RouterConfig/${routerId}/interfaces`);
        setInterfaces(data.data || []);
      }
      catch (catchError) {
          Sentry.captureException(catchError);
          /* ignore */
      }
    })();
  }, [routerId, apiCall]);

  const handleExecute = async () => {
    setRunning(true); setError(null);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/wizard/nat-masquerade`, {
        method: 'POST', body: JSON.stringify(form)
      });
      setResult('success'); onSuccess?.();
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setResult('error');
    }
    finally { setRunning(false); }
  };

  return (
    <div className="wizard">
      <div className="wizard__header">
        <FaExchangeAlt className="wizard__icon" />
        <div><h3>NAT Masquerade Wizard</h3><p>Configure source NAT masquerade for internet access</p></div>
        <button className="btn-icon wizard__close" onClick={onClose}><FaTimes /></button>
      </div>
      <div className="wizard__progress">
        {['Configure', 'Confirm'].map((label, i) => (
          <div key={i} className={`wizard__step ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : ''}`}>
            <span className="wizard__step-num">{step > i + 1 ? <FaCheck /> : i + 1}</span>
            <span className="wizard__step-label">{label}</span>
          </div>
        ))}
      </div>
      <div className="wizard__body">
        {result ? (
          <div className={`wizard__result wizard__result--${result}`}>
            {result === 'success' ? (
              <><FaCheckCircle size={32} /><h4>NAT Masquerade Configured</h4>
                <p>Traffic leaving via <strong>{form.outInterface}</strong> will now be masqueraded.</p></>
            ) : (<><FaExclamationTriangle size={32} /><h4>Wizard Failed</h4><p>{error}</p></>)}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : step === 1 ? (
          <div className="wizard__form">
            <div className="wizard__info" style={{ marginBottom: 16 }}>
              <FaInfoCircle /> NAT masquerade translates the source IP of outgoing packets to the router's WAN IP. This is required for LAN clients to access the internet.
            </div>
            <div className="form-group">
              <label>WAN / Out Interface</label>
              <select value={form.outInterface} onChange={e => setForm(f => ({ ...f, outInterface: e.target.value }))}>
                <option value="">Select WAN interface...</option>
                {interfaces.filter(i => i.type !== 'loopback').map(i => (
                  <option key={i.name} value={i.name}>{i.name} ({i.type})</option>
                ))}
              </select>
              <small className="muted">The interface connected to your ISP / internet</small>
            </div>
            <div className="form-group">
              <label>Source Address List (optional)</label>
              <input value={form.srcAddressList} onChange={e => setForm(f => ({ ...f, srcAddressList: e.target.value }))} placeholder="Leave empty for all traffic" />
              <small className="muted">If set, only addresses in this list will be masqueraded</small>
            </div>
          </div>
        ) : (
          <div className="wizard__summary">
            <h4>NAT Masquerade Summary</h4>
            <div className="summary-grid">
              <div className="summary-item"><span className="summary-label">Out Interface</span><span className="summary-value">{form.outInterface}</span></div>
              {form.srcAddressList && <div className="summary-item"><span className="summary-label">Src Address List</span><span className="summary-value mono">{form.srcAddressList}</span></div>}
            </div>
            <div className="wizard__info"><FaInfoCircle /> This will create a single srcnat masquerade rule on the router.</div>
          </div>
        )}
      </div>
      {!result && (
        <div className="wizard__footer">
          {step > 1 && <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
          <div className="wizard__footer-spacer" />
          {step < 2 ? (
            <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!form.outInterface}>Next <FaChevronRight /></button>
          ) : (
            <button className="btn btn-primary" onClick={handleExecute} disabled={running}>
              {running ? <><FaSpinner className="spin" /> Applying...</> : <><FaMagic /> Apply NAT</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Basic Firewall Wizard
// =====================================================================
const BasicFirewallWizard = ({ routerId, onClose, onSuccess }) => {
  const apiCall = useApiCall();
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [interfaces, setInterfaces] = useState([]);
  const [form, setForm] = useState({
    lanInterface: '',
    vpnInterface: '',
    allowIcmp: true,
    allowDns: false
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await apiCall(`/api/RouterConfig/${routerId}/interfaces`);
        setInterfaces(data.data || []);
      }
      catch (catchError) {
          Sentry.captureException(catchError);
          /* ignore */
      }
    })();
  }, [routerId, apiCall]);

  const handleExecute = async () => {
    setRunning(true); setError(null);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/wizard/basic-firewall`, {
        method: 'POST', body: JSON.stringify(form)
      });
      setResult('success'); onSuccess?.();
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setResult('error');
    }
    finally { setRunning(false); }
  };

  const rulePreview = [];
  rulePreview.push('Accept established/related/untracked');
  rulePreview.push('Drop invalid connections');
  if (form.allowIcmp) rulePreview.push('Accept ICMP (ping)');
  if (form.allowDns) rulePreview.push('Accept DNS queries (UDP+TCP/53)');
  rulePreview.push('Accept loopback (127.0.0.0/8)');
  if (form.lanInterface) rulePreview.push(`Accept all from LAN (${form.lanInterface})`);
  if (form.vpnInterface) rulePreview.push(`Accept all from VPN (${form.vpnInterface})`);
  rulePreview.push('Drop everything else');

  return (
    <div className="wizard">
      <div className="wizard__header">
        <FaShieldAlt className="wizard__icon" />
        <div><h3>Basic Firewall Hardening</h3><p>Apply a secure input-chain firewall ruleset with full rollback on failure</p></div>
        <button className="btn-icon wizard__close" onClick={onClose}><FaTimes /></button>
      </div>
      <div className="wizard__progress">
        {['Options', 'Review', 'Apply'].map((label, i) => (
          <div key={i} className={`wizard__step ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : ''}`}>
            <span className="wizard__step-num">{step > i + 1 ? <FaCheck /> : i + 1}</span>
            <span className="wizard__step-label">{label}</span>
          </div>
        ))}
      </div>
      <div className="wizard__body">
        {result ? (
          <div className={`wizard__result wizard__result--${result}`}>
            {result === 'success' ? (
              <><FaCheckCircle size={32} /><h4>Firewall Rules Applied</h4>
                <p>{rulePreview.length} rules were created on the input chain. The router is now hardened against unauthorized access.</p>
                <div className="wizard__info"><FaInfoCircle /> All rules are tagged with &quot;CapTrac Firewall Wizard&quot; for easy identification.</div></>
            ) : (<><FaExclamationTriangle size={32} /><h4>Wizard Failed — All Rules Rolled Back</h4><p>{error}</p>
                <div className="wizard__info"><FaInfoCircle /> No partial changes remain on the router. You can safely retry.</div></>)}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : step === 1 ? (
          <div className="wizard__form">
            <div className="wizard__info" style={{ marginBottom: 16 }}>
              <FaInfoCircle /> This wizard creates a standard input-chain firewall. It blocks all unsolicited input traffic except what you explicitly allow below.
            </div>
            <div className="form-group">
              <label>LAN Interface (trusted)</label>
              <select value={form.lanInterface} onChange={e => setForm(f => ({ ...f, lanInterface: e.target.value }))}>
                <option value="">— None —</option>
                {interfaces.filter(i => i.type !== 'loopback').map(i => (
                  <option key={i.name} value={i.name}>{i.name} ({i.type})</option>
                ))}
              </select>
              <small className="muted">All traffic from this interface will be accepted</small>
            </div>
            <div className="form-group">
              <label>VPN Interface (trusted)</label>
              <select value={form.vpnInterface} onChange={e => setForm(f => ({ ...f, vpnInterface: e.target.value }))}>
                <option value="">— None —</option>
                {interfaces.filter(i => i.type !== 'loopback').map(i => (
                  <option key={i.name} value={i.name}>{i.name} ({i.type})</option>
                ))}
              </select>
              <small className="muted">WireGuard, L2TP, or other VPN tunnel interface</small>
            </div>
            <div className="form-row-2">
              <label className="checkbox-label">
                <input type="checkbox" checked={form.allowIcmp} onChange={e => setForm(f => ({ ...f, allowIcmp: e.target.checked }))} />
                Allow ICMP (ping)
              </label>
              <label className="checkbox-label">
                <input type="checkbox" checked={form.allowDns} onChange={e => setForm(f => ({ ...f, allowDns: e.target.checked }))} />
                Allow DNS queries (port 53)
              </label>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="wizard__summary">
            <h4>Rule Preview (in order)</h4>
            <ol className="rule-preview">
              {rulePreview.map((r, i) => <li key={i}>{r}</li>)}
            </ol>
            <div className="wizard__warning">
              <FaExclamationTriangle /> <strong>Warning:</strong> This will add rules to the <em>input</em> chain. Ensure your management access (API port, SSH) will not be blocked.
              {form.lanInterface ? <> Your LAN interface <strong>{form.lanInterface}</strong> is trusted.</> : <> No LAN interface is set — make sure you have another way in.</>}
            </div>
          </div>
        ) : (
          <div className="wizard__summary">
            <h4>Ready to Apply</h4>
            <p>{rulePreview.length} firewall rules will be created. If any rule fails to create, <strong>all previously created rules will be automatically removed</strong> (full rollback).</p>
            <div className="wizard__info"><FaInfoCircle /> This is safe — no partial configuration will be left on the router.</div>
          </div>
        )}
      </div>
      {!result && (
        <div className="wizard__footer">
          {step > 1 && <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
          <div className="wizard__footer-spacer" />
          {step < 3 ? (
            <button className="btn btn-primary" onClick={() => setStep(s => s + 1)}>Next <FaChevronRight /></button>
          ) : (
            <button className="btn btn-primary" onClick={handleExecute} disabled={running}>
              {running ? <><FaSpinner className="spin" /> Applying...</> : <><FaMagic /> Apply Firewall Rules</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// WireGuard Wizard
// =====================================================================
const WireGuardWizard = ({ routerId, onClose, onSuccess }) => {
  const apiCall = useApiCall();
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [form, setForm] = useState({
    interfaceName: 'wg0',
    listenPort: 13231,
    tunnelAddress: '',
    peerPublicKey: '',
    peerEndpoint: '',
    peerAllowedAddresses: '0.0.0.0/0',
    addFirewallRule: true
  });

  const canProceed = () => {
    if (step === 1) return !!form.tunnelAddress;
    return true;
  };

  const handleExecute = async () => {
    setRunning(true); setError(null);
    try {
      const res = await apiCall(`/api/RouterConfig/${routerId}/wizard/wireguard`, {
        method: 'POST', body: JSON.stringify(form)
      });
      setGeneratedKey(res.publicKey);
      setResult('success'); onSuccess?.();
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setResult('error');
    }
    finally { setRunning(false); }
  };

  const copyKey = () => {
    if (generatedKey) navigator.clipboard?.writeText(generatedKey);
  };

  return (
    <div className="wizard">
      <div className="wizard__header">
        <FaLock className="wizard__icon" />
        <div><h3>WireGuard VPN Tunnel</h3><p>Create a WireGuard interface with auto-generated keys, IP assignment, and firewall rule</p></div>
        <button className="btn-icon wizard__close" onClick={onClose}><FaTimes /></button>
      </div>
      <div className="wizard__progress">
        {['Interface', 'Peer', 'Confirm'].map((label, i) => (
          <div key={i} className={`wizard__step ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : ''}`}>
            <span className="wizard__step-num">{step > i + 1 ? <FaCheck /> : i + 1}</span>
            <span className="wizard__step-label">{label}</span>
          </div>
        ))}
      </div>
      <div className="wizard__body">
        {result ? (
          <div className={`wizard__result wizard__result--${result}`}>
            {result === 'success' ? (
              <>
                <FaCheckCircle size={32} /><h4>WireGuard Tunnel Created</h4>
                <p>Interface <strong>{form.interfaceName}</strong> is active on port <strong>{form.listenPort}</strong>.</p>
                {generatedKey && (
                  <div className="wizard__key-display">
                    <label>Router&apos;s Public Key (use this on the peer side):</label>
                    <div className="key-row">
                      <code>{generatedKey}</code>
                      <button className="btn btn-sm btn-outline" onClick={copyKey} title="Copy"><FaCopy /></button>
                    </div>
                  </div>
                )}
                {result === 'success' && (
                  <div className="wizard__info"><FaInfoCircle /> If any step had failed, all created resources would have been automatically rolled back.</div>
                )}
              </>
            ) : (<><FaExclamationTriangle size={32} /><h4>Wizard Failed — Rolled Back</h4><p>{error}</p>
              <div className="wizard__info"><FaInfoCircle /> All partially created resources have been cleaned up.</div></>)}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : step === 1 ? (
          <div className="wizard__form">
            <div className="form-row-2">
              <div className="form-group">
                <label>Interface Name</label>
                <input value={form.interfaceName} onChange={e => setForm(f => ({ ...f, interfaceName: e.target.value }))} placeholder="wg0" />
              </div>
              <div className="form-group">
                <label>Listen Port</label>
                <input type="number" value={form.listenPort} onChange={e => setForm(f => ({ ...f, listenPort: parseInt(e.target.value) || 13231 }))} />
              </div>
            </div>
            <div className="form-group">
              <label>Tunnel IP Address (CIDR)</label>
              <input value={form.tunnelAddress} onChange={e => setForm(f => ({ ...f, tunnelAddress: e.target.value }))} placeholder="10.0.0.1/24" />
              <small className="muted">The IP this router will use inside the WireGuard tunnel</small>
            </div>
            <label className="checkbox-label">
              <input type="checkbox" checked={form.addFirewallRule} onChange={e => setForm(f => ({ ...f, addFirewallRule: e.target.checked }))} />
              Add firewall rule to allow WireGuard port (UDP/{form.listenPort})
            </label>
          </div>
        ) : step === 2 ? (
          <div className="wizard__form">
            <div className="wizard__info" style={{ marginBottom: 16 }}>
              <FaInfoCircle /> Peer configuration is optional. You can add peers later. Keys are auto-generated by RouterOS.
            </div>
            <div className="form-group">
              <label>Peer Public Key (optional)</label>
              <input value={form.peerPublicKey} onChange={e => setForm(f => ({ ...f, peerPublicKey: e.target.value }))} placeholder="Base64 public key of the remote peer" />
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Peer Endpoint (optional)</label>
                <input value={form.peerEndpoint} onChange={e => setForm(f => ({ ...f, peerEndpoint: e.target.value }))} placeholder="peer.example.com or IP" />
                <small className="muted">Leave empty if the peer connects to us</small>
              </div>
              <div className="form-group">
                <label>Allowed Addresses</label>
                <input value={form.peerAllowedAddresses} onChange={e => setForm(f => ({ ...f, peerAllowedAddresses: e.target.value }))} placeholder="0.0.0.0/0" />
                <small className="muted">Comma-separated CIDRs the peer is allowed to send from</small>
              </div>
            </div>
          </div>
        ) : (
          <div className="wizard__summary">
            <h4>WireGuard Configuration Summary</h4>
            <div className="summary-grid">
              <div className="summary-item"><span className="summary-label">Interface</span><span className="summary-value">{form.interfaceName}</span></div>
              <div className="summary-item"><span className="summary-label">Listen Port</span><span className="summary-value">{form.listenPort}</span></div>
              <div className="summary-item"><span className="summary-label">Tunnel IP</span><span className="summary-value mono">{form.tunnelAddress}</span></div>
              <div className="summary-item"><span className="summary-label">Firewall Rule</span><span className="summary-value">{form.addFirewallRule ? 'Yes' : 'No'}</span></div>
              {form.peerPublicKey && <div className="summary-item"><span className="summary-label">Peer Key</span><span className="summary-value mono" style={{ fontSize: '0.7rem' }}>{form.peerPublicKey.substring(0, 20)}...</span></div>}
            </div>
            <div className="wizard__info"><FaInfoCircle /> Keys are auto-generated. The router&apos;s public key will be shown after creation for peer configuration.</div>
          </div>
        )}
      </div>
      {!result && (
        <div className="wizard__footer">
          {step > 1 && <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
          <div className="wizard__footer-spacer" />
          {step < 3 ? (
            <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} disabled={!canProceed()}>Next <FaChevronRight /></button>
          ) : (
            <button className="btn btn-primary" onClick={handleExecute} disabled={running}>
              {running ? <><FaSpinner className="spin" /> Creating...</> : <><FaMagic /> Create WireGuard Tunnel</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Port Forward Wizard
// =====================================================================
const PortForwardWizard = ({ routerId, onClose, onSuccess }) => {
  const apiCall = useApiCall();
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [interfaces, setInterfaces] = useState([]);
  const [form, setForm] = useState({
    protocol: 'tcp',
    dstPort: '',
    toAddress: '',
    toPort: '',
    inInterface: '',
    hairpinNat: false,
    srcAddressRange: ''
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await apiCall(`/api/RouterConfig/${routerId}/interfaces`);
        setInterfaces(data.data || []);
      }
      catch (catchError) {
          Sentry.captureException(catchError);
          /* ignore */
      }
    })();
  }, [routerId, apiCall]);

  const canProceed = () => {
    if (step === 1) return !!form.dstPort && !!form.toAddress && !!form.toPort;
    return true;
  };

  const handleExecute = async () => {
    setRunning(true); setError(null);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/wizard/port-forward`, {
        method: 'POST', body: JSON.stringify(form)
      });
      setResult('success'); onSuccess?.();
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setResult('error');
    }
    finally { setRunning(false); }
  };

  return (
    <div className="wizard">
      <div className="wizard__header">
        <FaExchangeAlt className="wizard__icon" />
        <div><h3>Port Forward Wizard</h3><p>Create a dst-nat port forward with optional hairpin NAT for LAN access</p></div>
        <button className="btn-icon wizard__close" onClick={onClose}><FaTimes /></button>
      </div>
      <div className="wizard__progress">
        {['Forwarding', 'Options', 'Confirm'].map((label, i) => (
          <div key={i} className={`wizard__step ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : ''}`}>
            <span className="wizard__step-num">{step > i + 1 ? <FaCheck /> : i + 1}</span>
            <span className="wizard__step-label">{label}</span>
          </div>
        ))}
      </div>
      <div className="wizard__body">
        {result ? (
          <div className={`wizard__result wizard__result--${result}`}>
            {result === 'success' ? (
              <><FaCheckCircle size={32} /><h4>Port Forward Created</h4>
                <p>External port <strong>{form.protocol.toUpperCase()}/{form.dstPort}</strong> now forwards to <strong>{form.toAddress}:{form.toPort}</strong>.
                {form.hairpinNat && ' Hairpin NAT is enabled for LAN-to-LAN access.'}</p></>
            ) : (<><FaExclamationTriangle size={32} /><h4>Wizard Failed — Rolled Back</h4><p>{error}</p></>)}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : step === 1 ? (
          <div className="wizard__form">
            <div className="form-row-2">
              <div className="form-group">
                <label>Protocol</label>
                <select value={form.protocol} onChange={e => setForm(f => ({ ...f, protocol: e.target.value }))}>
                  <option value="tcp">TCP</option>
                  <option value="udp">UDP</option>
                </select>
              </div>
              <div className="form-group">
                <label>External Port</label>
                <input value={form.dstPort} onChange={e => setForm(f => ({ ...f, dstPort: e.target.value }))} placeholder="8080" />
              </div>
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Internal IP Address</label>
                <input value={form.toAddress} onChange={e => setForm(f => ({ ...f, toAddress: e.target.value }))} placeholder="192.168.1.100" />
              </div>
              <div className="form-group">
                <label>Internal Port</label>
                <input value={form.toPort} onChange={e => setForm(f => ({ ...f, toPort: e.target.value }))} placeholder="80" />
              </div>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="wizard__form">
            <div className="form-group">
              <label>In Interface (optional — restrict to specific WAN)</label>
              <select value={form.inInterface} onChange={e => setForm(f => ({ ...f, inInterface: e.target.value }))}>
                <option value="">— Any interface —</option>
                {interfaces.filter(i => i.type !== 'loopback').map(i => (
                  <option key={i.name} value={i.name}>{i.name} ({i.type})</option>
                ))}
              </select>
            </div>
            <label className="checkbox-label">
              <input type="checkbox" checked={form.hairpinNat} onChange={e => setForm(f => ({ ...f, hairpinNat: e.target.checked }))} />
              Enable hairpin NAT (LAN clients can reach the service via the external IP)
            </label>
            {form.hairpinNat && (
              <div className="form-group">
                <label>LAN Source Address Range</label>
                <input value={form.srcAddressRange} onChange={e => setForm(f => ({ ...f, srcAddressRange: e.target.value }))} placeholder="192.168.1.0/24" />
                <small className="muted">Required for hairpin — the subnet of your LAN clients</small>
              </div>
            )}
          </div>
        ) : (
          <div className="wizard__summary">
            <h4>Port Forward Summary</h4>
            <div className="summary-grid">
              <div className="summary-item"><span className="summary-label">Protocol</span><span className="summary-value">{form.protocol.toUpperCase()}</span></div>
              <div className="summary-item"><span className="summary-label">External Port</span><span className="summary-value mono">{form.dstPort}</span></div>
              <div className="summary-item"><span className="summary-label">Internal Target</span><span className="summary-value mono">{form.toAddress}:{form.toPort}</span></div>
              {form.inInterface && <div className="summary-item"><span className="summary-label">In Interface</span><span className="summary-value">{form.inInterface}</span></div>}
              <div className="summary-item"><span className="summary-label">Hairpin NAT</span><span className="summary-value">{form.hairpinNat ? 'Yes' : 'No'}</span></div>
            </div>
            <div className="wizard__info"><FaInfoCircle /> If hairpin NAT fails, the dst-nat rule is automatically rolled back.</div>
          </div>
        )}
      </div>
      {!result && (
        <div className="wizard__footer">
          {step > 1 && <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
          <div className="wizard__footer-spacer" />
          {step < 3 ? (
            <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} disabled={!canProceed()}>Next <FaChevronRight /></button>
          ) : (
            <button className="btn btn-primary" onClick={handleExecute} disabled={running}>
              {running ? <><FaSpinner className="spin" /> Creating...</> : <><FaMagic /> Create Port Forward</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// DNS Setup Wizard
// =====================================================================
const DnsSetupWizard = ({ routerId, onClose, onSuccess }) => {
  const apiCall = useApiCall();
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    servers: '8.8.8.8,8.8.4.4',
    allowRemoteRequests: true,
    cacheSizeMb: '',
    staticEntries: []
  });

  const addStaticEntry = () => setForm(f => ({
    ...f, staticEntries: [...f.staticEntries, { name: '', address: '', ttl: '' }]
  }));
  const removeStaticEntry = (idx) => setForm(f => ({
    ...f, staticEntries: f.staticEntries.filter((_, i) => i !== idx)
  }));
  const updateStaticEntry = (idx, field, val) => setForm(f => ({
    ...f, staticEntries: f.staticEntries.map((e, i) => i === idx ? { ...e, [field]: val } : e)
  }));

  const handleExecute = async () => {
    setRunning(true); setError(null);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/wizard/dns-setup`, {
        method: 'POST',
        body: JSON.stringify({
          servers: form.servers,
          allowRemoteRequests: form.allowRemoteRequests,
          cacheSizeMb: form.cacheSizeMb ? parseInt(form.cacheSizeMb) : null,
          staticEntries: form.staticEntries.filter(e => e.name && e.address)
        })
      });
      setResult('success'); onSuccess?.();
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setResult('error');
    }
    finally { setRunning(false); }
  };

  return (
    <div className="wizard">
      <div className="wizard__header">
        <FaGlobe className="wizard__icon" />
        <div><h3>DNS Setup Wizard</h3><p>Configure upstream DNS servers, caching, and static entries</p></div>
        <button className="btn-icon wizard__close" onClick={onClose}><FaTimes /></button>
      </div>
      <div className="wizard__progress">
        {['Servers', 'Static Entries', 'Confirm'].map((label, i) => (
          <div key={i} className={`wizard__step ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : ''}`}>
            <span className="wizard__step-num">{step > i + 1 ? <FaCheck /> : i + 1}</span>
            <span className="wizard__step-label">{label}</span>
          </div>
        ))}
      </div>
      <div className="wizard__body">
        {result ? (
          <div className={`wizard__result wizard__result--${result}`}>
            {result === 'success' ? (
              <><FaCheckCircle size={32} /><h4>DNS Configured Successfully</h4>
                <p>Upstream servers set to <strong>{form.servers}</strong>.
                {form.staticEntries.filter(e => e.name).length > 0 && ` ${form.staticEntries.filter(e => e.name).length} static entries added.`}</p></>
            ) : (<><FaExclamationTriangle size={32} /><h4>Wizard Failed</h4><p>{error}</p>
              <div className="wizard__info"><FaInfoCircle /> Any successfully created static entries were rolled back.</div></>)}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : step === 1 ? (
          <div className="wizard__form">
            <div className="form-group">
              <label>Upstream DNS Servers</label>
              <input value={form.servers} onChange={e => setForm(f => ({ ...f, servers: e.target.value }))} placeholder="8.8.8.8,8.8.4.4" />
              <small className="muted">Comma-separated list of upstream DNS server IPs</small>
            </div>
            <label className="checkbox-label">
              <input type="checkbox" checked={form.allowRemoteRequests} onChange={e => setForm(f => ({ ...f, allowRemoteRequests: e.target.checked }))} />
              Allow remote requests (router acts as DNS server for LAN clients)
            </label>
            <div className="form-group">
              <label>Cache Size (KiB, optional)</label>
              <input type="number" value={form.cacheSizeMb} onChange={e => setForm(f => ({ ...f, cacheSizeMb: e.target.value }))} placeholder="2048 (default)" />
            </div>
          </div>
        ) : step === 2 ? (
          <div className="wizard__form">
            <div className="wizard__info" style={{ marginBottom: 16 }}>
              <FaInfoCircle /> Add static DNS entries for local name resolution (e.g., internal servers, printers).
            </div>
            {form.staticEntries.map((entry, idx) => (
              <div key={idx} className="form-row-2" style={{ alignItems: 'flex-end' }}>
                <div className="form-group">
                  <label>Hostname</label>
                  <input value={entry.name} onChange={e => updateStaticEntry(idx, 'name', e.target.value)} placeholder="server.local" />
                </div>
                <div className="form-group">
                  <label>IP Address</label>
                  <input value={entry.address} onChange={e => updateStaticEntry(idx, 'address', e.target.value)} placeholder="192.168.1.10" />
                </div>
                <button className="btn btn-sm btn-danger" onClick={() => removeStaticEntry(idx)} style={{ marginBottom: 12 }}><FaTimes /></button>
              </div>
            ))}
            <button className="btn btn-sm btn-outline" onClick={addStaticEntry}>+ Add Static Entry</button>
          </div>
        ) : (
          <div className="wizard__summary">
            <h4>DNS Configuration Summary</h4>
            <div className="summary-grid">
              <div className="summary-item"><span className="summary-label">DNS Servers</span><span className="summary-value mono">{form.servers}</span></div>
              <div className="summary-item"><span className="summary-label">Allow Remote</span><span className="summary-value">{form.allowRemoteRequests ? 'Yes' : 'No'}</span></div>
              {form.cacheSizeMb && <div className="summary-item"><span className="summary-label">Cache Size</span><span className="summary-value">{form.cacheSizeMb} KiB</span></div>}
              <div className="summary-item"><span className="summary-label">Static Entries</span><span className="summary-value">{form.staticEntries.filter(e => e.name).length}</span></div>
            </div>
            <div className="wizard__info"><FaInfoCircle /> If any static entry fails, previously created entries are rolled back. DNS server settings are applied first.</div>
          </div>
        )}
      </div>
      {!result && (
        <div className="wizard__footer">
          {step > 1 && <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>Back</button>}
          <div className="wizard__footer-spacer" />
          {step < 3 ? (
            <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} disabled={step === 1 && !form.servers}>Next <FaChevronRight /></button>
          ) : (
            <button className="btn btn-primary" onClick={handleExecute} disabled={running}>
              {running ? <><FaSpinner className="spin" /> Applying...</> : <><FaMagic /> Apply DNS Configuration</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Wizard Launcher — shows available wizards
// =====================================================================
const RouterWizards = ({ routerId }) => {
  const [activeWizard, setActiveWizard] = useState(null);

  const wizards = [
    {
      id: 'dhcp', icon: <FaServer />, title: 'DHCP Server Setup',
      description: 'Configure a complete DHCP server with IP pool, network settings, and DNS — all in one step. Rolls back automatically if any step fails.',
      tags: ['DHCP', 'Pool', 'Network', 'Rollback']
    },
    {
      id: 'nat', icon: <FaExchangeAlt />, title: 'NAT Masquerade',
      description: 'Set up source NAT masquerade so LAN clients can reach the internet through a WAN interface.',
      tags: ['NAT', 'Internet', 'srcnat']
    },
    {
      id: 'firewall', icon: <FaShieldAlt />, title: 'Basic Firewall Hardening',
      description: 'Apply a standard input-chain firewall ruleset (accept established, drop invalid, allow LAN/VPN, drop all). Full rollback on failure.',
      tags: ['Firewall', 'Security', 'Hardening', 'Rollback']
    },
    {
      id: 'failover', icon: <FaRoute />, title: 'Internet Failover',
      description: 'Configure automatic failover between two internet breakouts using recursive routing with check-gateway. Rolls back on failure.',
      tags: ['Failover', 'Routing', 'Redundancy', 'Rollback']
    },
    {
      id: 'wireguard', icon: <FaLock />, title: 'WireGuard VPN Tunnel',
      description: 'Create a WireGuard interface with auto-generated keys, IP assignment, optional peer, and firewall allow rule. Full rollback.',
      tags: ['WireGuard', 'VPN', 'Tunnel', 'Rollback']
    },
    {
      id: 'portforward', icon: <FaExchangeAlt />, title: 'Port Forwarding',
      description: 'Create a dst-nat port forward rule with optional hairpin NAT for LAN-to-LAN access. Rolls back if hairpin fails.',
      tags: ['NAT', 'Port Forward', 'dst-nat', 'Rollback']
    },
    {
      id: 'dns', icon: <FaGlobe />, title: 'DNS Setup',
      description: 'Configure upstream DNS servers, enable caching, and add static DNS entries for local name resolution. Static entries roll back on failure.',
      tags: ['DNS', 'Caching', 'Static Entries', 'Rollback']
    },
    {
      id: 'vrrp', icon: <FaShieldAlt />, title: 'VRRP Hot Standby',
      description: 'Configure VRRP between two routers so a backup takes over instantly if the master fails. Can auto-configure the peer.',
      tags: ['VRRP', 'HA', 'Redundancy']
    }
  ];

  return (
    <div className="router-wizards">
      {activeWizard === 'dhcp' && (
        <DhcpWizard routerId={routerId} onClose={() => setActiveWizard(null)} onSuccess={() => {}} />
      )}
      {activeWizard === 'nat' && (
        <NatMasqueradeWizard routerId={routerId} onClose={() => setActiveWizard(null)} onSuccess={() => {}} />
      )}
      {activeWizard === 'firewall' && (
        <BasicFirewallWizard routerId={routerId} onClose={() => setActiveWizard(null)} onSuccess={() => {}} />
      )}
      {activeWizard === 'failover' && (
        <FailoverWizard routerId={routerId} onClose={() => setActiveWizard(null)} onSuccess={() => {}} />
      )}
      {activeWizard === 'wireguard' && (
        <WireGuardWizard routerId={routerId} onClose={() => setActiveWizard(null)} onSuccess={() => {}} />
      )}
      {activeWizard === 'portforward' && (
        <PortForwardWizard routerId={routerId} onClose={() => setActiveWizard(null)} onSuccess={() => {}} />
      )}
      {activeWizard === 'dns' && (
        <DnsSetupWizard routerId={routerId} onClose={() => setActiveWizard(null)} onSuccess={() => {}} />
      )}
      {activeWizard === 'vrrp' && (
        <VrrpWizard routerId={routerId} onClose={() => setActiveWizard(null)} onSuccess={() => {}} />
      )}

      {!activeWizard && (
        <>
          <div className="wizards-header">
            <FaMagic className="wizards-header__icon" />
            <div>
              <h3>Configuration Wizards</h3>
              <p>Streamlined setup for common MikroTik configurations. Each wizard walks you through the process step-by-step and applies changes via the RouterOS API.</p>
            </div>
          </div>
          <div className="wizard-cards">
            {wizards.map(w => (
              <div key={w.id} className="wizard-card" onClick={() => setActiveWizard(w.id)}>
                <div className="wizard-card__icon">{w.icon}</div>
                <div className="wizard-card__content">
                  <h4>{w.title}</h4>
                  <p>{w.description}</p>
                  <div className="wizard-card__tags">
                    {w.tags.map(t => <span key={t} className="badge badge-type">{t}</span>)}
                  </div>
                </div>
                <FaChevronRight className="wizard-card__arrow" />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RouterWizards;
